import type { RouteDescriptor } from "catchment-common/interfaces/navigation";
import {
  agentRouteCatalog,
  type RouteId
} from "catchment-common/interfaces/navigation/routeCatalog";

// Route URL shapes derive from the shared catalog by convention: a route's path
// is `/<url>` plus one `/:param` per declared param (in order). The `url` field
// IS the URL's first segment, so there's no template to hand-write — adding a
// catalog route needs no edit here. Routes whose URL doesn't follow the
// convention set an explicit override below.
const ROUTE_TEMPLATE_OVERRIDES: Partial<Record<RouteId, string>> = {};

// Only path-kind params become `/:param` segments. Query/hash params live in
// the URL's search/hash, not the path, so they must not widen the template
// (e.g. settings' `tab` query param must keep the template at `/settings`).
const deriveTemplate = (route: RouteDescriptor): string =>
  `/${route.url}${(route.params ?? [])
    .filter((p) => !p.kind || p.kind === "path")
    .map((p) => `/:${p.name}`)
    .join("")}`;

export const ROUTE_TEMPLATES = Object.fromEntries(
  (agentRouteCatalog as readonly RouteDescriptor[]).map((r) => [
    r.url,
    ROUTE_TEMPLATE_OVERRIDES[r.url as RouteId] ?? deriveTemplate(r)
  ])
) as Record<RouteId, string>;

// Substitute :params in a template. Missing params keep their placeholder, so
// the same helper builds real URLs and the ":param" templates used to register
// routes. Plain replacement (no encoding) keeps placeholder round-trips intact.
export const fillTemplate = (
  template: string,
  params: Record<string, string | undefined>
): string =>
  template.replace(
    /:([A-Za-z0-9_]+)/g,
    (_match, key: string) => params[key] ?? `:${key}`
  );

//Public routes
export const login = "/login";
export const register = "/register";
export const forgotPassword = "/forgot-password";
export const apiDocs = "/api-docs";
export const mcpDocs = "/mcp-docs";
export const claudeDocs = "/claude-docs";
export const employeeRegister = (token?: string) =>
  absolute(["employee-register", token]);

//Private routes
export const settings = "/settings";
export const sroiBuilder = "/sroi-builder";
export const sroiBuilderCreate = "/sroi-builder/create";
export const sroiBuilderReport = (id: string) => `/sroi-builder/report/${id}`;

export const createAccount = "/create-account";
export const lastProject = "/last-project";
export const projects = ROUTE_TEMPLATES["projects"];
export const employees = ROUTE_TEMPLATES["employees"];
export const organisationDashboard = ROUTE_TEMPLATES["organisation-dashboard"];

export const socialProAdmin = "/social-pro-admin";

const projectScoped =
  (routeId: RouteId) =>
  (projectId: string, contractorPackageId: string): string =>
    fillTemplate(ROUTE_TEMPLATES[routeId], {
      contractorPackageId,
      projectId
    });

export const forecast = projectScoped("forecast");
export const contractorDashboard = projectScoped("dashboard");
export const packages = projectScoped("packages");
export const analyticsSpendLevel1 = projectScoped("analytics-spend");
export const analyticsLabourHoursLevel1 = projectScoped(
  "analytics-labour-hours"
);
export const contractorPackageReports = projectScoped("monthly-reports");
export const projectReports = projectScoped("project-reports");
export const projectDetails = projectScoped("project-details");

export const organisationDocumentsLevel1 = "/organisation-documents";

// The organisation's Export Generator, the same page the project has at
// `createExport`, reading across the organisation's projects instead of one
// project's packages.
export const organisationExportGenerator = "/organisation-export-generator";

// The organisation's framework Library — authoring moved up from the project;
// see CLAUDE.md and `.scratch/framework-library/HANDOFF.md`. Plain strings, the
// same convention `organisationDocumentsLevel1` and `sroiBuilder` use, not
// catalog routes.
export const frameworkLibrary = "/framework-library";
export const frameworkLibraryCreate = "/framework-library/create";
export const frameworkLibraryEdit = (familyId?: string) =>
  absolute(["framework-library", familyId, "edit"]);

/**
 * A framework's version history — every version of one family, newest first.
 *
 * The same two-level shape the Documents Register already uses
 * (`organisationDocumentsLevel1` / `Level2`), which is the same model: a family
 * of revisions listed at its latest, with the history one level down. Static
 * segments outrank dynamic ones in the router, so this cannot swallow
 * `/framework-library/create`.
 */
export const frameworkLibraryVersions = (familyId?: string) =>
  absolute(["framework-library", familyId]);

/**
 * Org-level Compliance: one page per framework family, reached from the
 * sidebar's Compliance rows — the organisation-level sibling of the project's
 * `analyticsCompliance` pages.
 */
export const organisationCompliance = (frameworkId?: string) =>
  absolute(["organisation-compliance", frameworkId]);

// Org-level Insights (analytics clone). Plain strings — not catalog routes.
export const orgInsightsLabourHours = "/organisation-insights/labour-hours";
export const orgInsightsSocialSpend = "/organisation-insights/social-spend";
export const orgInsightsLocalContent = "/organisation-insights/local-content";
// GovAssure children — one Insights page per policy category, mirroring the
// project-scoped `analyticsGovAssure*` split.
export const orgInsightsDocuments = "/organisation-insights/documents";
export const orgInsightsForms = "/organisation-insights/forms";
export const orgInsightsRegisters = "/organisation-insights/registers";
export const orgInsightsSurveys = "/organisation-insights/surveys";
// EnviroTrack children — each environmental type is its own Insights page,
// mirroring SocialPro's per-family split (see `analyticsEmissionsLevel1` and
// siblings below for the project-scoped equivalents).
export const orgInsightsEmissions = "/organisation-insights/emissions";
export const orgInsightsEnergy = "/organisation-insights/energy";
export const orgInsightsWater = "/organisation-insights/water";
export const orgInsightsWaste = "/organisation-insights/waste";
export const orgInsightsMaterials = "/organisation-insights/materials";
export const orgInsightsTransport = "/organisation-insights/transport";

export const organisationDocumentsLevel2 = (familyId?: string) =>
  absolute(["organisation-documents", familyId]);

export const projectSetup = (projectId?: string) =>
  absolute(["project-setup", projectId]);

export const employeeSetup = (employeeId?: string) =>
  absolute(["employee-setup", employeeId]);

export const projectEmployeeSetupSubmission = (
  projectId: string,
  contractorPackageId: string,
  reportId: string,
  employeeId?: string
) =>
  absolute([
    "project-employee-setup-submission",
    projectId,
    contractorPackageId,
    reportId,
    employeeId
  ]);

export const projectEmployeeSetup = (
  projectId: string,
  contractorPackageId: string,
  employeeId: string
) =>
  absolute([
    "project-employee-setup",
    projectId,
    contractorPackageId,
    employeeId
  ]);

export const forecastProjectLevel1 = (projectId: string) =>
  absolute(["forecast", projectId]);

export const forecastLevel1 = (
  projectId: string,
  contractorPackageId: string
) => absolute(["forecast", projectId, contractorPackageId]);

export const contractorDashboardPackage = (
  projectId: string,
  contractorPackageId: string
) => absolute(["dashboard", projectId, contractorPackageId, "package"]);

export const packageSetup = (
  projectId: string,
  contractorPackageId: string,
  packageNumber: string,
  subPackageId?: string
) =>
  absolute([
    "package-setup",
    projectId,
    contractorPackageId,
    packageNumber,
    subPackageId
  ]);

export const projectEmployees = (
  projectId: string,
  contractorPackageId: string
) => absolute(["project-employees", projectId, contractorPackageId]);

export const contractorPackage = (
  projectId: string,
  contractorPackageId: string,
  packageId: string
) => absolute(["package-details", projectId, contractorPackageId, packageId]);

// EnviroTrack children — one URL builder per environmental type, mirroring
// SocialPro's per-family split (`analyticsLabourHoursLevel1`,
// `analyticsSpendLevel1`, `analyticsLocalContent` above). Each type is its own
// sidebar child and route; the Overview/Breakdown/Targets sub-tabs inside it
// are local component state, not routes.
export const analyticsEmissionsLevel1 = (
  projectId: string,
  contractorPackageId: string
) => absolute(["analytics-emissions", projectId, contractorPackageId]);

export const analyticsEnergyLevel1 = (
  projectId: string,
  contractorPackageId: string
) => absolute(["analytics-energy", projectId, contractorPackageId]);

export const analyticsWaterLevel1 = (
  projectId: string,
  contractorPackageId: string
) => absolute(["analytics-water", projectId, contractorPackageId]);

export const analyticsWasteLevel1 = (
  projectId: string,
  contractorPackageId: string
) => absolute(["analytics-waste", projectId, contractorPackageId]);

export const analyticsMaterialsLevel1 = (
  projectId: string,
  contractorPackageId: string
) => absolute(["analytics-materials", projectId, contractorPackageId]);

export const analyticsTransportLevel1 = (
  projectId: string,
  contractorPackageId: string
) => absolute(["analytics-transport", projectId, contractorPackageId]);

export const analyticsLocalContent = (
  projectId: string,
  contractorPackageId: string
) => absolute(["analytics-local-content", projectId, contractorPackageId]);

export const analyticsDocumentsLevel1 = (
  projectId: string,
  contractorPackageId: string
) => absolute(["analytics-documents", projectId, contractorPackageId]);

// Compliance is scoped to one framework, so it carries a third coordinate: the
// framework's catalogue id (e.g. "VIC-4"), not the per-project row uuid. The
// catalogue id is globally unique and stable across projects, which keeps the
// URL readable and makes a shared link mean the same framework everywhere.
export const analyticsCompliance = (
  projectId: string,
  contractorPackageId: string,
  frameworkId: string
) =>
  absolute([
    "analytics-compliance",
    projectId,
    contractorPackageId,
    frameworkId
  ]);

export const analyticsDocumentsLevel2 = (
  projectId: string,
  contractorPackageId: string,
  documentId: string
) =>
  absolute(["analytics-documents", projectId, contractorPackageId, documentId]);

export const analyticsDocumentsLevel3 = (
  projectId: string,
  contractorPackageId: string,
  subPackageId: string,
  documentId: string
) =>
  absolute([
    "analytics-documents",
    projectId,
    contractorPackageId,
    subPackageId,
    documentId
  ]);

export const analyticsGovAssureDocuments = (
  projectId: string,
  contractorPackageId: string
) =>
  absolute(["analytics-govassure-documents", projectId, contractorPackageId]);

export const analyticsGovAssureForms = (
  projectId: string,
  contractorPackageId: string
) => absolute(["analytics-govassure-forms", projectId, contractorPackageId]);

export const analyticsGovAssureSurveys = (
  projectId: string,
  contractorPackageId: string
) => absolute(["analytics-govassure-surveys", projectId, contractorPackageId]);

export const analyticsGovAssureRegisters = (
  projectId: string,
  contractorPackageId: string
) =>
  absolute(["analytics-govassure-registers", projectId, contractorPackageId]);

export const subpackageReports = (
  projectId: string,
  contractorPackageId: string
) => absolute(["project-reports", projectId, contractorPackageId]);

export const createExport = (projectId: string, contractorPackageId: string) =>
  absolute(["create-export", projectId, contractorPackageId]);

export const monthlyReportSubmission = (
  projectId: string,
  contractorPackageId: string,
  reportId: string
) =>
  absolute([
    "monthly-report-submission",
    projectId,
    reportId,
    contractorPackageId
  ]);

export const readOnlyMonthlyReport = (
  projectId: string,
  contractorPackageId: string,
  packageId: string,
  reportId: string
) =>
  absolute([
    "view-monthly-report",
    projectId,
    contractorPackageId,
    packageId,
    reportId
  ]);

export const readOnlyPackageReport = (
  projectId: string,
  contractorPackageId: string,
  packageId: string,
  reportId: string
) =>
  absolute([
    "view-package-report",
    projectId,
    contractorPackageId,
    packageId,
    reportId
  ]);

export const viewProjectReport = (
  projectId: string,
  contractorPackageId: string,
  reportId: string
) =>
  absolute(["view-project-report", projectId, contractorPackageId, reportId]);

// Per-section reviewer page: edit that one section's rows or accept it.
// `section` is a ReportSubmissionMessageType value (e.g. "Hour").
export const reviewReportSection = (
  projectId: string,
  contractorPackageId: string,
  packageId: string,
  reportId: string,
  section: string
) =>
  absolute([
    "review-report-section",
    projectId,
    contractorPackageId,
    packageId,
    reportId,
    section
  ]);

export const reviewPackageReportSection = (
  projectId: string,
  contractorPackageId: string,
  packageId: string,
  reportId: string,
  section: string
) =>
  absolute([
    "review-package-report-section",
    projectId,
    contractorPackageId,
    packageId,
    reportId,
    section
  ]);

export const smartDirectory = (
  projectId: string,
  contractorPackageId: string,
  tagetId?: string
) => absolute(["smart-directory", projectId, contractorPackageId, tagetId]);

export const absolute = (params: (string | number | undefined)[]): string =>
  `/${params.filter((a) => Boolean(a)).join("/")}`;
